import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';
import BusinessComparisonTable from '../components/BusinessComparisonTable';
import { Button } from '../components/ui/button';
import { 
  Check, 
  Clock, 
  FileText, 
  Users, 
  ArrowRight, 
  ArrowLeft,
  Receipt,
  AlertCircle,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { getServiceBySlug } from '../data/serviceContent';
import { openWhatsApp } from '../utils/whatsappUtils';

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(id);

  // Check if this is a Business Setup page
  const isBusinessSetup = ['proprietorship', 'partnership', 'llp', 'private-limited', 'opc'].includes(id);

  const handleCTA = () => {
    // Open WhatsApp with service-specific context
    openWhatsApp(id, service?.name);
  };

  // Dynamic CTA text based on service type
  const getCTAText = () => {
    return 'Get Started';
  };

  // If service not found
  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <StickyHeader />
        <div className="container mx-auto px-4 md:px-8 py-24 text-center">
          <h1 className="text-2xl font-medium mb-4 text-slate-900">Service not found</h1>
          <p className="text-slate-500 mb-8">This service page is being updated.</p>
          <Button onClick={() => navigate('/pricing')} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to services
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="service-detail-page">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 md:px-8 py-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Link to="/pricing" className="hover:text-slate-600 transition-colors">Services</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/pricing?category=${service.categorySlug}`} className="hover:text-slate-600 transition-colors">
            {service.category}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-600">{service.name}</span>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          {/* Conditional layout: 2-column for all, but different sticky behavior */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
            
            {/* Left Column - Content */}
            <div className="lg:col-span-2">
              
              {/* Hero */}
              <div className="mb-12">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {service.category}
                </span>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2 mb-4 text-slate-900" data-testid="service-name">
                  {service.name}
                </h1>
                <p className="text-base text-slate-500 leading-relaxed" data-testid="service-overview">
                  {service.overview}
                </p>
              </div>

              {/* Who Should Choose */}
              <div className="mb-10">
                <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  Who should choose this
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5">
                  {service.whoShouldChoose.map((item, idx) => (
                    <div key={`who-${id}-${idx}`} className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included */}
              <div className="mb-10">
                <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-500" />
                  What's included
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5">
                  {service.whatsIncluded.map((item, idx) => (
                    <div key={`included-${id}-${idx}`} className="flex items-start gap-2.5" data-testid={`included-${idx}`}>
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600 leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <div className="mb-10">
                <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-slate-500" />
                  How it works
                </h2>
                <div className="flex flex-col md:flex-row gap-4 md:gap-3 mt-5">
                  {service.howItWorks.slice(0, 4).map((step, idx) => (
                    <div key={`step-${id}-${idx}`} className="flex-1 relative">
                      <div className="flex md:flex-col items-start md:items-center gap-3 md:gap-2">
                        <span className="h-8 w-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
                          {idx + 1}
                        </span>
                        <p className="text-sm text-slate-600 md:text-center leading-snug">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents Required */}
              <div className="mb-10">
                <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  Documents required
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2">
                  {service.documentsRequired.map((doc, idx) => (
                    <div key={`doc-${id}-${idx}`} className="flex items-center gap-2.5">
                      <span className="h-1 w-1 bg-slate-300 rounded-full flex-shrink-0" />
                      <span className="text-sm text-slate-500">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Taxation Structure - only for business setup services */}
              {service.taxation && service.taxation.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-slate-500" />
                    Taxation structure
                  </h2>
                  <div className="flex flex-col gap-2">
                    {service.taxation.map((item, idx) => (
                      <div key={`tax-${id}-${idx}`} className="flex items-center gap-2.5">
                        <span className="h-1 w-1 bg-slate-300 rounded-full flex-shrink-0" />
                        <span className="text-sm text-slate-500">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Not Included */}
              {service.notIncluded && service.notIncluded.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5 text-slate-500" />
                    Not included
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {service.notIncluded.map((item, idx) => (
                      <span key={`notincl-${id}-${idx}`} className="text-xs text-slate-500">
                        {item}{idx < service.notIncluded.length - 1 && <span className="ml-2 text-slate-300">•</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comparison Table - Mobile/Tablet only (inside left column) */}
              {isBusinessSetup && (
                <div className="lg:hidden">
                  <BusinessComparisonTable currentServiceSlug={id} />
                </div>
              )}
            </div>

            {/* Right Column - Pricing Sidebar */}
            <div className="lg:col-span-1">
              {/* Non-sticky for Business Setup, sticky for others */}
              <div className={`p-5 bg-white border border-slate-200 rounded-xl shadow-sm ${isBusinessSetup ? '' : 'lg:sticky lg:top-24'}`} data-testid="pricing-card">
                
                {/* Timeline */}
                <div className="pb-4 mb-5 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="h-4 w-4" />
                    <span data-testid="service-timeline">{service.timeline}</span>
                  </div>
                  {/* Timeline disclaimer - positioned directly below processing time */}
                  {service.timelineNote && (
                    <p className="text-[11px] text-slate-400 mt-1.5 ml-6">
                      {service.timelineNote}
                    </p>
                  )}
                </div>

                {/* You Pay Now - Primary emphasis */}
                <div className="mb-5">
                  <p className="text-xs text-slate-400 mb-1">Professional Fee (Payable Now)</p>
                  <p className="text-2xl font-semibold text-slate-900" data-testid="service-price">
                    ₹{service.pricing.professional.toLocaleString()}
                    <span className="text-sm font-normal text-slate-400 ml-1">(incl. taxes)</span>
                  </p>
                </div>

                {/* Government Fees - Secondary, informational */}
                <div className="mb-5 py-2.5 px-3 bg-slate-50/50 rounded-md">
                  <p className="text-[11px] text-slate-400 mb-1">Government Fees</p>
                  <p className="text-xs text-slate-500 font-normal" data-testid="govt-fees">
                    {service.pricing.governmentFees}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {service.pricing.governmentFees.toLowerCase().includes('actual') || service.pricing.governmentFees.toLowerCase().includes('varies')
                      ? 'Depends on state, category, or department'
                      : 'Paid directly to authorities during filing'
                    }
                  </p>
                </div>

                {/* Primary CTA */}
                <Button 
                  className="w-full mb-1.5 rounded-lg" 
                  size="default"
                  onClick={handleCTA}
                  data-testid="primary-cta"
                >
                  {getCTAText()} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                
                {/* CTA clarification */}
                <p className="text-[10px] text-center text-slate-400 mb-3">
                  You are paying only professional fees
                </p>
                
                {/* Secondary links */}
                <Link to="/pricing" className="block">
                  <Button variant="ghost" className="w-full text-slate-500 hover:text-slate-700" size="sm" data-testid="view-all-services">
                    View all services
                  </Button>
                </Link>

                <p className="text-[10px] text-center text-slate-400 mt-2">
                  Clear scope • Transparent pricing
                </p>
              </div>
            </div>
          </div>

          {/* Full-width Comparison Table - Desktop only for Business Setup */}
          {isBusinessSetup && (
            <div className="hidden lg:block mt-12">
              <BusinessComparisonTable currentServiceSlug={id} />
            </div>
          )}
        </div>
      </section>

      {/* Related Services CTA - Simplified */}
      <section className="py-14 bg-slate-50/80 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl text-center">
          <h2 className="text-xl font-medium text-slate-800 mb-2">
            Not quite what you need?
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            Tell us your situation — we'll recommend the right service.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleCTA} size="sm" className="gap-1.5">
              Describe your situation <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            <Link to={`/pricing?category=${service.categorySlug}`}>
              <Button variant="outline" size="sm">
                Browse {service.category}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
